import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

export function createPostHogAnalyticsService(
  props: {
    key: string;
    host?: string;
  } = {
    key: POSTHOG_KEY!,
    host: POSTHOG_HOST,
  },
) {
  const key = props.key || POSTHOG_KEY;
  const host = props.host || POSTHOG_HOST;

  if (!key) {
    throw new Error(
      'POSTHOG_KEY is not set. Please set the environment variable NEXT_PUBLIC_POSTHOG_KEY.',
    );
  }

  return new PostHogAnalyticsService(key, host);
}

class PostHogAnalyticsService {
  private initialized = false;

  constructor(
    private readonly key: string,
    private readonly host: string = 'https://eu.i.posthog.com',
  ) {}

  async initialize() {
    if (this.initialized) {
      return Promise.resolve();
    }

    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    posthog.init(this.key, {
      api_host: '/ingest',
      ui_host: this.host,
    });

    // Explicitly set window.posthog for PostHog Surveys to work
    // The posthog-js library doesn't automatically set this when using ES module imports
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;

    this.initialized = true;
  }

  async trackPageView(path: string) {
    await this.initialize();

    if (!this.initialized) {
      return;
    }

    posthog.capture('$pageview', {
      $current_url: path,
    });
  }

  async trackEvent(
    eventName: string,
    eventProperties: Record<string, string | string[]> = {},
  ) {
    await this.initialize();

    if (!this.initialized) {
      return;
    }

    posthog.capture(eventName, eventProperties);
  }

  async identify(userId: string, traits: Record<string, string> = {}) {
    await this.initialize();

    if (!this.initialized) {
      return;
    }

    posthog.identify(userId, traits);
  }
}
